// disable eslint check for new-cap because every openlayer object is lowercase
/* eslint-disable new-cap */

import olInteractionSelect from 'ol/interaction/Select'
import olFeature from 'ol/Feature'
import olFormatWkt from 'ol/format/WKT'
import olControlScaleLine from 'ol/control/ScaleLine'
import olSourceTilewms from 'ol/source/TileWMS'
import WMTS, { optionsFromCapabilities } from 'ol/source/WMTS'
import olLayerTile from 'ol/layer/Tile'
import { fromLonLat, get as getProjection } from 'ol/proj'
import olSourceXyz from 'ol/source/XYZ'
import { createForProjection } from 'ol/tilegrid'
import * as olExtent from 'ol/extent'
import TileState from 'ol/TileState'

import OSM from 'ol/source/OSM'

import wmsScaleUtils from '@/utils/wmsScaleUtils'

// Keyed by layer so a second tick while the first request is still running joins that request
// instead of starting another one.
const pendingSourceLoads = new WeakMap()

// Keyed by capabilities URL: several layers share one service, and the IGN document alone is 3 MB.
const capabilitiesByUrl = new Map()

// Past its deepest level for an area, ESRI does not fail the request but answers with a grey
// 'Map data not yet available' picture. That depth differs per area (imagery stops at 19 in Lyon and
// at 21 in Utrecht), so no single maxZoom fits. The picture is always this many bytes, and the size
// is the one thing about it a cross-origin fetch can read.
const ESRI_NO_DATA_TILE_SIZE = 2521

/**
 * Load an ESRI tile, failing it when ESRI only has its no-data picture. A failed tile is what makes
 * OpenLayers (useInterimTilesOnError) stretch the deepest tile it did get over that spot, so zooming
 * on past the data keeps the last real picture as background.
 */
function loadEsriTile (tile, src) {
  fetch(src)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Tile responded with status ${response.status}`)
      }
      return response.blob()
    })
    .then((blob) => {
      if (blob.size === ESRI_NO_DATA_TILE_SIZE) {
        tile.setState(TileState.ERROR)
        return
      }

      // A blob URL is same-origin, so the tile does not taint the canvas when the map is exported.
      const image = tile.getImage()
      const objectUrl = URL.createObjectURL(blob)
      const revoke = () => URL.revokeObjectURL(objectUrl)
      image.addEventListener('load', revoke, { once: true })
      image.addEventListener('error', revoke, { once: true })
      image.src = objectUrl
    })
    .catch(() => {
      tile.setState(TileState.ERROR)
    })
}

/**
 * The default WMS tile grid, cut off at the deepest level the server still draws. Past the last
 * level of its grid OpenLayers enlarges that level's tiles, so zooming in further keeps the last
 * real picture instead of the empty images the server sends there.
 */
function createWmsTileGrid (projectionCode, minScaleDenominator) {
  const projection = getProjection(projectionCode)
  const maxZoom = wmsScaleUtils.deepestZoomForMinScale(
    createForProjection(projection).getResolutions(),
    projection.getMetersPerUnit(),
    minScaleDenominator
  )

  return createForProjection(projection, maxZoom)
}

function fetchCapabilities (capabilitiesUrl, capabilitiesParser) {
  if (!capabilitiesByUrl.has(capabilitiesUrl)) {
    const capabilities = fetch(capabilitiesUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`GetCapabilities responded with status ${response.status}`)
        }
        return response.text()
      })
      .then((capabilitiesXml) => capabilitiesParser.read(capabilitiesXml))
      .catch((error) => {
        capabilitiesByUrl.delete(capabilitiesUrl)
        throw error
      })

    capabilitiesByUrl.set(capabilitiesUrl, capabilities)
  }

  return capabilitiesByUrl.get(capabilitiesUrl)
}

export default {
  /**
   * Returns the layer object
   */
  getLayerByName (name, map) {
    let layer

    map.getLayers().forEach(function (lyr) {
      let layerName = lyr.get('name')
      if (name === layerName) {
        layer = lyr
      }
    })

    return layer
  },

  /**
   * Get the feature with ID 'featureID' in layer 'layer'.
   * @param {ol/layer} the layer
   * @param {string} the ID of the feature
   */
  getFeature (layer, featureID) {
    if (typeof layer !== 'undefined') {
      return layer.getSource().getFeatureById(featureID)
    }
  },

  /**
   * returns the interaction object
   */
  getInteraction (interactionType, map) {
    let retInteraction
    map.getInteractions().forEach(function (interaction) {
      if (interaction instanceof interactionType) {
        retInteraction = interaction
      }
    })

    return retInteraction
  },

  /**
   * Change the selection to the given feature (or none if not found)
   */
  selectFeature (layerName, featureID, map) {
    let layer = this.getLayerByName(layerName, map)
    let feature = this.getFeature(layer, featureID)

    let selectInteraction = this.getInteraction(olInteractionSelect, map)
    selectInteraction.getFeatures().clear()

    if (feature) {
      selectInteraction.getFeatures().push(feature)
    }

    return feature
  },

  /**
   * Add feature 'feature' to layer with name 'layerName'.
   * @param {string} layerName the name of the layer
   * @param {ol/Feature} feature the feature to be added
   */
  addFeature (layerName, feature, map) {
    let layer = this.getLayerByName(layerName, map)

    if (typeof layer !== 'undefined') {
      layer.getSource().addFeature(feature)
    }
  },

  /*
    add features like scale lines, north arrows, wind socks.. whatever
    */
  addOverlayFeatures (map) {
    // eslint-disable-next-line new-cap
    const scaleLine = new olControlScaleLine()
    map.addControl(scaleLine)
    map.scaleLineRef = scaleLine
  },

  /**
   * Places a marker on the map as an OpenLayers.Feature.Vector .
   * @param {ol.geom.Point} position - The position where the marker has to be placed (has to be in mercator projection)
   * @param {iconStyleEnum} iconStyle - The style of the marker to be placed
   * @param {object} [markerData] - The data that is to attached to the marker
   */
  createMarker (position, iconStyle, markerData) {
    markerData = markerData || {}

    // eslint-disable-next-line new-cap
    let marker = new olFeature({
      geometry: position
    })

    marker.setStyle(iconStyle)

    // cannot set via constructor :(
    if (markerData.id) {
      marker.setId(markerData.id)
    }

    return marker
  },

  /**
   * Remove all features from the select interaction and the layer with name 'layerName'.
   * @param {string} layerName the name of the layer
   */
  removeAllFeatures (layerName, map) {
    let selectInteraction = this.getInteraction(olInteractionSelect, map)

    if (selectInteraction) {
      selectInteraction.getFeatures().clear()
    }

    let layer = this.getLayerByName(layerName, map)

    if (layer) {
      layer.getSource().clear()
    }
  },

  /**
   * Focus (zoom in) on feature with id 'featureID' in layer with name 'layerName'.
   * @param {string} layerName the name of the layer
   * @param {string} featureID the ID of the feature
   */
  focusOnFeature (layerName, featureID, map) {
    // focus on feature
    let layer = this.getLayerByName(layerName, map)

    let feature = this.getFeature(layer, featureID)

    if (feature === null) return

    // keep current zoom level if the user is zoomed in
    let currentZoom = map.getView().getZoom()
    if (currentZoom < 18) {
      map.getView().setZoom(18)
    }

    let extent = feature.getGeometry().getExtent()
    let center = olExtent.getCenter(extent)
    map.getView().animate({ center: center })
  },

  /**
   * Focus on and select a measurementpoint.
   * @param {string} mpID Either the MpID or MpGuid of the measuremenpoint to be focussed on.
   */
  focusOnMP (mpID, measurementPoints, map) {
    this.focusOnFeature('map.layer.projectData.measurementPointLayer', mpID, map)
    this.selectFeature('map.layer.projectData.measurementPointLayer', mpID, map)
  },

  /**
   * Strips the z coordinate from a wktstring (if present) and returns it as a geometry in the correct projection
   * @param {string} wktString - geometry string in EPSG:4326
   * @return {string} - geometry string in EPSG: 3857 without z coordinate
   */
  readWKTString (wktString) {
    // hacky way to remove 3th argument
    // since this is not needed by OL3
    if (wktString.includes('POINT')) {
      let coordinates = wktString.substring(
        wktString.lastIndexOf('(') + 1,
        wktString.lastIndexOf(')')
      )
      let split = coordinates.split(' ')

      if (split.length > 2) {
        wktString = 'POINT (' + split[0] + ' ' + split[1] + ')'
      }
    }

    // parse geometry
    // eslint-disable-next-line new-cap
    let format = new olFormatWkt()
    let geom = format.readGeometry(wktString)

    geom.transform('EPSG:4326', 'EPSG:3857')

    return geom
  },

  /**
   * Returns the z coordinate of a POINT wkt string, or null when it carries only x and y.
   * readWKTString drops z on the way in, so this is what carries the height across a move.
   */
  readWKTStringHeight (wktString) {
    if (wktString != null && wktString.includes('POINT')) {
      let coordinates = wktString.substring(
        wktString.lastIndexOf('(') + 1,
        wktString.lastIndexOf(')')
      )
      let split = coordinates.split(' ')

      if (split.length > 2) {
        return split[2]
      } else {
        return null
      }
    } else {
      return null
    }
  },

  /**
   * Reverses readWKTString()
   */
  writeWKTString (geometry, zCoordinate) {
    // eslint-disable-next-line new-cap
    let format = new olFormatWkt()
    // clone the geometry since we don't want to transform the real geometry
    // THIS IS REALLY IMPORTANT
    let geom = geometry.clone()
    geom.transform('EPSG:3857', 'EPSG:4326')
    let wktString = format.writeGeometry(geom)

    // add extra z-coordinate if it's defined and store in wkt-string
    if (zCoordinate && wktString.includes('POINT')) {
      wktString = wktString.substring(0, wktString.length - 1) + ' ' + zCoordinate + ')'
    }

    return wktString
  },

  convertToOLLayerObject (obj, objName) {
    let subLayers = obj.sublayers.reduce((obj, item) => {
      obj[item['id']] = {
        title: item['title'],
        visible: false
      }

      return obj
    }, {})

    let source = {
      url: obj.url,
      projection: obj.EPSG || 'EPSG:3857',
      transition: 0,
      params: {
        CRS: obj.EPSG || 'EPSG:3857'
      },
      crossOrigin: 'anonymous'
    }
    // eslint-disable-next-line new-cap
    return new olLayerTile(
      Object.assign(
        {
          name: obj.name,
          category: obj.country,
          editable: true,
          visible: false,
          invertMapTextColour: obj.invertMapTextColour,
          opacity: 1,
          opacityPercentage: 100,
          subLayers: subLayers,
          viewparams: obj['viewparams'],
          CQL_FILTER: obj['CQL_FILTER'],
          preload: Infinity,
          source: null,
          lazySource: { wms: source, minScaleDenominator: obj.minScaleDenominator }
        },
        objName
      )
    )
  },

  convertToOLWmtsLayerObject (layerConfig, savedLayerSettings) {
    const capabilitiesUrl = new URL(layerConfig.url)
    capabilitiesUrl.searchParams.set('service', 'WMTS')
    capabilitiesUrl.searchParams.set('request', 'GetCapabilities')

    // eslint-disable-next-line new-cap
    return new olLayerTile(
      Object.assign(
        {
          name: layerConfig.name,
          category: layerConfig.country,
          editable: true,
          visible: false,
          invertMapTextColour: layerConfig.invertMapTextColour,
          opacity: 1,
          opacityPercentage: 100,
          preload: Infinity,
          source: null,
          lazySource: {
            capabilitiesUrl: capabilitiesUrl.toString(),
            crossOrigin: 'anonymous',
            options: {
              layer: layerConfig.layer || (layerConfig.sublayers && layerConfig.sublayers[0] && layerConfig.sublayers[0].id),
              matrixSet: layerConfig.matrixSet || layerConfig.EPSG || 'EPSG:3857',
              style: layerConfig.style || 'default',
              format: layerConfig.format || 'image/png'
            }
          }
        },
        savedLayerSettings
      )
    )
  },

  /**
   * Build the Topotijdreis year layers without a source. Every year is its own WMTS service, so
   * filling them all in up front costs one GetCapabilities request per year — 178 of them, before
   * the map itself is created. loadLazySource() fetches the one the user actually ticks.
   * @param {Array} yearConfigs the mapLayers array from topoTijdReis.json
   */
  createTopoTijdreisLayers (yearConfigs) {
    return yearConfigs
      .map((yearConfig) => {
        const isSingleYear = yearConfig.from === yearConfig.to

        // eslint-disable-next-line new-cap
        return new olLayerTile({
          category: 'TijdreisNL',
          opacity: 1,
          className: 'ol-layer',
          opacityPercentage: 100,
          editable: true,
          visible: false,
          name: isSingleYear ? `${yearConfig.to}` : `${yearConfig.from}-${yearConfig.to}`,
          source: null,
          lazySource: {
            capabilitiesUrl: `${yearConfig.url}/WMTS/1.0.0/WMTSCapabilities.xml`,
            options: {
              layer: isSingleYear ? `Historische_tijdreis_${yearConfig.to}` : `Historische_tijdreis_${yearConfig.from}_${yearConfig.to}`,
              matrixSet: 'EPSG:28992'
            }
          }
        })
      })
      .sort((a, b) => parseInt(b.values_.name) - parseInt(a.values_.name))
  },

  /**
   * Attach the source of a layer that was created without one, fetching its capabilities when it
   * needs them. Resolves immediately for a layer that already has its source, so callers can call
   * it on every tick.
   * @param {ol/layer} layer a layer carrying a lazySource description
   * @param {ol/format/WMTSCapabilities} capabilitiesParser
   */
  loadLazySource (layer, capabilitiesParser) {
    const lazySource = layer.values_.lazySource
    if (!lazySource) {
      return Promise.resolve()
    }

    // A WMS source is built rather than fetched: it is the tile grid and the tile cache that cost
    // something, and the server is only asked for tiles once the layer is drawn.
    if (lazySource.wms) {
      const sourceOptions = lazySource.minScaleDenominator
        ? Object.assign({ tileGrid: createWmsTileGrid(lazySource.wms.projection, lazySource.minScaleDenominator) }, lazySource.wms)
        : lazySource.wms

      // eslint-disable-next-line new-cap
      layer.setSource(new olSourceTilewms(sourceOptions))
      layer.values_.lazySource = null

      return Promise.resolve()
    }

    const runningLoad = pendingSourceLoads.get(layer)
    if (runningLoad) {
      return runningLoad
    }

    const load = fetchCapabilities(lazySource.capabilitiesUrl, capabilitiesParser)
      .then((capabilities) => {
        const sourceOptions = optionsFromCapabilities(capabilities, lazySource.options)

        if (!sourceOptions) {
          throw new Error(`Capabilities hold no layer ${lazySource.options.layer}`)
        }

        if (lazySource.crossOrigin) {
          sourceOptions.crossOrigin = lazySource.crossOrigin
        }

        // eslint-disable-next-line new-cap
        layer.setSource(new WMTS(sourceOptions))
        layer.values_.lazySource = null
      })
      .catch((error) => {
        // Forget the failed attempt so ticking the layer again retries it.
        pendingSourceLoads.delete(layer)
        throw error
      })

    pendingSourceLoads.set(layer, load)

    return load
  },

  convertBaseLayerToOLLayerObject (obj, mapLayersConfig) {
    let source
    let name
    switch (obj.source) {
      case 'OSM':
        name = 'map.layer.base.OSM'
        source = new OSM()
        break
      case 'ESRI':
        name = 'map.layer.base.ESRI.' + obj.name
        // eslint-disable-next-line new-cap
        source = new olSourceXyz(Object.assign({ tileLoadFunction: loadEsriTile }, obj.settings))
        break
      default:
        console.error('convertBaseLayerToOLLayerObject(): Invalid type!')
        return
    }

    source.crossOrigin = 'anonymous'

    // eslint-disable-next-line new-cap
    return new olLayerTile(
      Object.assign(
        {
          name: name,
          category: 'Base',
          visible: obj.defaultVisible,
          invertMapTextColour: obj.invertMapTextColour,
          opacity: 1,
          opacityPercentage: 100,
          editable: true,
          preload: Infinity,
          source: source
        },
        mapLayersConfig[name]
      )
    )
  },

  /**
   * Add right click on draw feature interaction. Opens context menu.
   * @param {*} ctx Open Layers map component
   */
  addContextMenuInteraction (ctx) {
    ctx.map.getViewport().addEventListener('contextmenu', async function (e) {
      e.preventDefault()
      const { layerX, layerY } = e

      // Editing - a right-click on a vertex removes it, and nothing else opens the menu mid-edit
      if (ctx.editingDrawFeature) {
        ctx.removeEditedContourVertex(ctx.map.getEventPixel(e))
        return
      }

      // Not drawing or placing & on feature - Pop menu
      if (!ctx.clickToDrawContour && !ctx.clickToPlaceMarker) {
        const featureArray = await ctx.drawLayer.getFeatures([layerX, layerY])
        if (featureArray.length === 0) return

        const feature = featureArray[0]
        ctx.clickToContextMenu = true
        ctx.position = { x: layerX, y: layerY }
        ctx.selectedDrawFeature = feature
      }

      // Drawing - Cancel
      if (ctx.clickToDrawContour) {
        ctx.drawFeature.abortDrawing()
      }
    })
  },
  search (query, map) {
    fetch(`https://nominatim.openstreetmap.org/search.php?q=` + encodeURIComponent(query) + `&format=jsonv2`)
      .then(response => {
        if (response.status === 200) {
          response.json().then(searchResults => {
            if (searchResults.length > 0) {
              var topResult = searchResults[0]
              var coordinates = fromLonLat([topResult.lon, topResult.lat], 'EPSG:3857')
              map.getView().setCenter(coordinates, map.getSize(), coordinates)
              map.getView().animate({
                zoom: 19,
                duration: 1500
              })
            }
          })
        }
      })
  }
}
